import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Microphone, MicrophoneSlash, X, Sparkle, WarningCircle, Ear, EarSlash, Question } from "@phosphor-icons/react";
import api from "../lib/api";
import { executeVoiceIntent, voiceSpeak } from "../lib/voiceCommander";
import { getSingletonAudio } from "../lib/singleAudio";
import { useAudio } from "../context/AudioContext";
import useDraggableFAB from "../hooks/useDraggableFAB";

// Floating "press to talk" microphone. Tap → start recognition. Stop
// is automatic when the user pauses for 0.8s, or when they tap again.
//
// Wake-word mode: when the user enables "Hey Holy Words" listening,
// a SECOND SpeechRecognition instance runs continuously in the
// background. It only fires the intent pipeline when the transcript
// contains the wake phrase. If anything follows the phrase ("Hey Holy
// Words, play Lecrae"), we submit just the tail. If the user said only
// the wake phrase ("Hey Holy Words"), we open the active-listen
// overlay so they can speak their next sentence normally.
//
// Why a separate recognizer for wake-word? Web Speech doesn't let
// you change `continuous` mid-session, and we want short-form active
// commands (continuous=false, fast end-of-speech detection) AND
// always-on wake listening (continuous=true, auto-restart) to
// coexist without stomping on each other.
//
// Why Web Speech API vs OpenAI Whisper for ASR:
//   • Web Speech is on-device on every modern browser (Chrome/Edge/
//     Safari/Opera/Android Chrome) — zero latency, zero API spend.
//   • Whisper would charge per minute and require uploading audio,
//     which we don't need for short voice commands.
// iOS Safari only added SpeechRecognition behind a flag in 14.3+ — we
// detect support and surface a friendly fallback if missing.
//
// Why a CustomEvent bus for player controls: see lib/voiceCommander.js.

const MAX_TRANSCRIPT_CHARS = 240;
const WAKE_STORAGE_KEY = "hw-voice-wake-enabled";
const HINT_DISMISSED_KEY = "hw-voice-hint-dismissed";
// Lets the user nuke the entire floating mic UI from their screen.
// Re-enable from Settings → Voice & Audio. Listened to globally
// (Settings page dispatches `hw:voice:visible` when toggled).
const HIDDEN_KEY = "hw-voice-fab-hidden";

// Voice-command examples shown in the in-app help drawer. Grouped by
// intent so users can scan quickly. Anything spoken in natural English
// that maps to one of these intents will work — these are just the
// canonical phrases.
const VOICE_EXAMPLES = [
  { group: "Music & video", items: [
    "Play Casting Crowns",
    "Play Lecrae",
    "Play Sunday A.M. Medley",
    "Play Travis Greene",
    "Play Danny Gokey",
  ]},
  { group: "Bible & Apocrypha", items: [
    "Read Genesis 3",
    "Read Psalm 23",
    "Read John chapter 1",
    "Apocrypha Tobit 1",
    "Jump to verse 12",
  ]},
  { group: "Movies", items: [
    "Play War Room",
    "Play The Encounter",
    "Play Fireproof",
  ]},
  { group: "Playback", items: [
    "Pause",
    "Resume",
    "Next",
    "Previous",
    "Speak slower",
    "Speak faster",
  ]},
  { group: "Navigation", items: [
    "Go to Sermons",
    "Go to Movies",
    "Open Memorial Wall",
    "Open Prayer Wall",
  ]},
];
// Accept a few natural mishears + the canonical phrase. Each entry is
// a regex; we test the LIVE transcript (lowercased) against all of
// them. Tail capture lets us run the remainder as an immediate
// command for hands-free chaining.
const WAKE_PATTERNS = [
  /\bhey,?\s+holy\s+words?\b[\s,.!?]*(.*)/i,
  /\bhey,?\s+holi\w*\b[\s,.!?]*(.*)/i,        // "hey holiness" / "hey holies" misher
  /\bok(?:ay)?,?\s+holy\s+words?\b[\s,.!?]*(.*)/i,
  /\bholy\s+words?[,.]?\s+(play|read|open|go|next|previous|prev|shuffle|loop|pause|resume|stop|skip|close).*/i,
];

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

// Try to extract a follow-on command from a transcript that contains
// the wake phrase. Returns "" when the user said the wake phrase alone.
function extractWakeTail(text) {
  const t = (text || "").trim();
  if (!t) return null;
  for (const pat of WAKE_PATTERNS) {
    const m = pat.exec(t);
    if (!m) continue;
    // Group 1 may be the captured verb (pattern 4) OR the trailing
    // command (patterns 1–3). Either way we splice from the match
    // index to the end and trim — that gives us the most useful
    // command string regardless of which pattern hit.
    const start = m.index + (m[0].toLowerCase().indexOf(m[1] ? m[1].toLowerCase() : "") || 0);
    const tail = (t.slice(start) || m[1] || "").trim();
    return tail;
  }
  return null;
}

export default function VoiceCommander() {
  const SR = getSpeechRecognition();
  const supported = !!SR;
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Make the floating VOICE pill drag-and-droppable. Long-press to
  // pick it up, drag anywhere on-screen, release to snap to nearest
  // edge. Position is remembered across sessions.
  //
  // Default position needs to clear the BottomNav (Home / Listen /
  // + / Today / You — ≈ 84-96px tall on mobile) AND the AudioBar
  // when it's open. Previously the default was `bottom: 20`, which
  // tucked the mic BEHIND the BottomNav so the user only saw it when
  // they started playing audio (which auto-shifted it up). Now we
  // hard-reserve 96px at the bottom via the useDraggableFAB hook so
  // the mic can never get parked behind the nav, and default to
  // `bottom: 110` so it lands clearly above it.
  const fab = useDraggableFAB({
    storageKey: "hw:fab:voice",
    defaultPosition: { right: 20, bottom: 110 },
    size: 56,
    bottomReserve: 96,
  });

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState(null); // {say, action, target}
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false); // overlay panel open
  const [busy, setBusy] = useState(false);
  // Wake-word state. Persisted in localStorage so it survives navigation.
  const [wakeEnabled, setWakeEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(WAKE_STORAGE_KEY) === "1"; } catch (_) { return false; }
  });
  const [wakeActive, setWakeActive] = useState(false); // recognizer actually running
  // Read-aloud in progress? The wake-word mic must YIELD while audio
  // plays: iOS cancels every speechSynthesis utterance the instant an
  // open SpeechRecognition session holds the audio input (Android is
  // tolerant — hence "works on Android, silent on iPhone").
  const { isPlaying: audioPlaying } = useAudio();
  const [showHelp, setShowHelp] = useState(false);
  // First-visit hint: a one-time speech bubble that points to the mic
  // so people actually know it's there. Dismissed forever after the
  // user taps it once or taps the mic itself.
  const [showHint, setShowHint] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(HINT_DISMISSED_KEY) !== "1"; } catch (_) { return false; }
  });
  const dismissHint = () => {
    setShowHint(false);
    try { window.localStorage.setItem(HINT_DISMISSED_KEY, "1"); } catch (_) {}
  };

  // Whole-feature hide. When true, the FAB + panel + green/amber
  // pulse indicators are unmounted entirely (and the wake-word
  // recognizer below is shut down). Re-enable from
  // Settings → Voice & Audio.
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      // One-shot un-hide migrations. Each version is run exactly once
      // per install. If we ever need to globally restore the mic for
      // every user again, just bump the version key.
      const RESTORE_KEY_V2 = "hw:voice-fab:restore-v2-2026-02-19";
      if (!window.localStorage.getItem(RESTORE_KEY_V2)) {
        window.localStorage.removeItem(HIDDEN_KEY);
        // Also clear the saved FAB position so the mic snaps back to
        // the default bottom-right corner — "reset my mic" should
        // mean both visible AND back where it started.
        try { window.localStorage.removeItem("hw:fab:voice"); } catch (_) {}
        window.localStorage.setItem(RESTORE_KEY_V2, "1");
        return false;
      }
      return window.localStorage.getItem(HIDDEN_KEY) === "1";
    } catch (_) { return false; }
  });
  // Listen for the Settings page toggling this on/off so users don't
  // have to refresh after re-enabling.
  useEffect(() => {
    const onChange = (e) => {
      const v = !!(e && e.detail && e.detail.hidden);
      setHidden(v);
    };
    window.addEventListener("hw:voice:hidden-changed", onChange);
    return () => window.removeEventListener("hw:voice:hidden-changed", onChange);
  }, []);

  const recRef = useRef(null);
  const wakeRecRef = useRef(null);
  const wakeRestartTimerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  // Resume function returned by pauseAllMedia(); we keep it in a ref so
  // close handlers (panel close, onend) can fire the resume even if the
  // user never spoke / cancelled mid-listen.
  const resumeMediaRef = useRef(null);

  const stopListening = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch (_) { /* noop */ }
      recRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setListening(false);
  }, []);

  // Resume any media we paused on mic-open. Safe to call multiple times
  // (the ref is cleared after one fire).
  const resumeMedia = useCallback(() => {
    if (resumeMediaRef.current) {
      try { resumeMediaRef.current(); } catch (_) {}
      resumeMediaRef.current = null;
    }
  }, []);

  // Persist wake preference.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(WAKE_STORAGE_KEY, wakeEnabled ? "1" : "0"); } catch (_) { /* noop */ }
  }, [wakeEnabled]);

  // Pause every active <video>/YT-iframe the moment the mic opens, so
  // background music doesn't get picked up as a voice command (which
  // was making the mic hear lyrics → route junk transcripts → trigger
  // accidental "next/skip" intents → fast-forward bug). Returns a
  // resume function the caller invokes on close.
  const pauseAllMedia = useCallback(() => {
    const resumers = [];
    // Pause every <audio>/<video> on the page.
    try {
      document.querySelectorAll("audio, video").forEach((el) => {
        // Skip the singleton TTS <audio> — voiceSpeak owns it and will
        // call stopOnce() itself. Without this guard we'd capture the
        // singleton here, voiceSpeak would start the confirmation TTS,
        // and on close we'd .play() the old track on top of the new
        // one → user hears two voices overlapping.
        try { if (el === getSingletonAudio()) return; } catch (_) {}
        if (!el.paused) {
          el.pause();
          resumers.push(() => { try { el.play(); } catch (_) {} });
        }
      });
    } catch (_) {}
    // Pause every YouTube IFrame via postMessage. We can't reliably
    // detect their playing state from outside, so we just send a pause
    // command — harmless on already-paused frames — and queue a play
    // resume on close. Limited to youtube/youtube-nocookie origins.
    try {
      document.querySelectorAll("iframe").forEach((fr) => {
        const src = fr.src || "";
        if (!/youtube(-nocookie)?\.com\/embed/.test(src)) return;
        try {
          fr.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
        } catch (_) {}
        resumers.push(() => {
          try { fr.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*"); } catch (_) {}
        });
      });
    } catch (_) {}
    return () => resumers.forEach((fn) => fn());
  }, []);

  // A transcript is "junk" when it doesn't contain any verb the intent
  // parser knows about AND looks more like song lyrics than a command.
  // We use this to short-circuit submissions captured from background
  // music (the music recognised-as-speech bug). Verbs come from the
  // intent router's grammar: play / pause / read / open / go / next /
  // previous / skip / stop / louder / softer / faster / slower /
  // jump / chapter / verse.
  const isJunkTranscript = useCallback((txt) => {
    const s = (txt || "").trim().toLowerCase();
    if (!s) return true;
    if (s.length < 4) return true;
    const verbs = /\b(play|pause|resume|read|open|go|navigate|next|prev|previous|skip|stop|mute|unmute|louder|softer|volume|faster|slower|jump|chapter|verse|search|find|hey holy|holy words)\b/;
    return !verbs.test(s);
  }, []);

  // Final-or-interim transcript handler — schedules a 0.8s silence
  // window after the latest result, so users don't have to manually
  // stop after every command.
  const onTranscript = useCallback((text, isFinal) => {
    setTranscript(text.slice(0, MAX_TRANSCRIPT_CHARS));
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (isFinal || text.trim()) {
      silenceTimerRef.current = setTimeout(() => {
        // Stop recognition; the onend handler will call submit().
        stopListening();
      }, 800);
    }
  }, [stopListening]);

  const submit = useCallback(async (text) => {
    const finalText = (text || "").trim();
    if (!finalText) return;
    // Junk filter: if the transcript looks like background music
    // lyrics (no command verb), don't fire an intent. This prevents
    // the "fast-forward" bug where the mic heard the playing song's
    // lyrics and the intent router translated random words into a
    // skip/next command.
    if (isJunkTranscript(finalText)) {
      setError("Hmm, I only caught background sound. Try again — say something like 'play Casting Crowns'.");
      voiceSpeak("");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/voice/intent", { transcript: finalText });
      const intent = res?.data || {};
      setResponse(intent);
      const ok = await executeVoiceIntent(intent, { navigate });
      if (!ok && (!intent.say || intent.action === "unknown")) {
        voiceSpeak("I'm not sure how to do that yet. Try 'play Lecrae' or 'read Genesis'.");
      }
      // Auto-close the panel a moment after success.
      setTimeout(() => { setOpen(false); resumeMedia(); }, 1600);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Voice command failed. Try again?";
      setError(msg);
      voiceSpeak("Sorry, something went wrong.");
    } finally {
      setBusy(false);
    }
  }, [navigate, isJunkTranscript, resumeMedia]);

  const startListening = useCallback(() => {
    if (!SR) {
      setError("Voice commands need Chrome, Edge, or Safari.");
      setOpen(true);
      return;
    }
    setError(null);
    setResponse(null);
    setTranscript("");
    setOpen(true);
    // Pause any active music/video BEFORE we open the mic so the
    // recogniser doesn't pick up song lyrics as a voice command.
    // Stored on a ref so resumeMedia() can fire whether the user
    // cancels, the command succeeds, or the panel closes.
    resumeMediaRef.current = pauseAllMedia();
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    rec.maxAlternatives = 1;
    rec.onstart = () => setListening(true);
    rec.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      const combined = (final || interim).trim();
      onTranscript(combined, !!final);
    };
    rec.onerror = (e) => {
      const code = e?.error || "unknown";
      if (code === "no-speech") setError("I didn't catch anything. Tap and try again.");
      else if (code === "not-allowed") setError("Microphone access blocked. Enable it in your browser settings.");
      else if (code === "audio-capture") setError("No microphone found.");
      else setError(`Voice error: ${code}`);
      setListening(false);
      // Resume music — we don't want it stuck paused after a mic
      // error.
      resumeMedia();
    };
    rec.onend = () => {
      setListening(false);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      // Use the latest transcript regardless of who called .stop()
      setTranscript((t) => {
        if (t && !busy) submit(t);
        else resumeMedia(); // nothing captured → unfreeze the music now
        return t;
      });
    };
    recRef.current = rec;
    try {
      rec.start();
    } catch (e) {
      setError("Couldn't start the mic. Try refreshing.");
      setListening(false);
    }
  }, [SR, onTranscript, submit, busy]);

  // Global hotkey: hold "/" → activate mic (skips when typing in inputs).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toUpperCase();
      if (["INPUT", "TEXTAREA"].includes(tag) || e.target?.isContentEditable) return;
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (listening) stopListening();
        else startListening();
      } else if (e.key === "Escape" && open) {
        setOpen(false);
        stopListening();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [listening, open, startListening, stopListening]);

  // Tear-down on unmount: stop any active mic, clear timers.
  useEffect(() => {
    return () => {
      stopListening();
      if (wakeRecRef.current) {
        try { wakeRecRef.current.abort(); } catch (_) { /* noop */ }
        wakeRecRef.current = null;
      }
      if (wakeRestartTimerRef.current) {
        clearTimeout(wakeRestartTimerRef.current);
        wakeRestartTimerRef.current = null;
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
      }
    };
  }, [stopListening]);

  // ── Wake-word recognizer lifecycle ─────────────────────────────
  // A second, continuous recognizer runs when wakeEnabled is true.
  // It auto-restarts whenever the browser ends a session (every
  // ~30–60s on Chrome). It pauses while the active mic is running so
  // we don't get duplicate recognition; resumes on `onend`.
  useEffect(() => {
    if (!SR) return undefined;
    if (!wakeEnabled || hidden || audioPlaying) {
      // Tear down whenever the user disables wake mode OR hides the
      // whole voice UI OR read-aloud audio is playing (see audioPlaying
      // note above). Without checking `hidden` here, the wake-word
      // recognizer kept running silently after the user hit "×",
      // which is exactly the "I can't get the green dot out" bug.
      if (wakeRecRef.current) {
        try { wakeRecRef.current.abort(); } catch (_) { /* noop */ }
        wakeRecRef.current = null;
      }
      if (wakeRestartTimerRef.current) {
        clearTimeout(wakeRestartTimerRef.current);
        wakeRestartTimerRef.current = null;
      }
      setWakeActive(false);
      return undefined;
    }

    let cancelled = false;
    const lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";

    const startWake = () => {
      if (cancelled) return;
      // Don't run if the active mic is currently engaged.
      if (recRef.current) {
        wakeRestartTimerRef.current = setTimeout(startWake, 500);
        return;
      }
      try {
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = lang;
        rec.maxAlternatives = 1;

        rec.onstart = () => setWakeActive(true);
        rec.onresult = (event) => {
          // Inspect every result (interim + final) for the wake phrase.
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const r = event.results[i];
            const text = r[0]?.transcript || "";
            const tail = extractWakeTail(text);
            if (tail === null) continue;
            // Confirmed wake. Stop the continuous recognizer so we
            // can open the focused mic cleanly without overlapping.
            try { rec.abort(); } catch (_) { /* noop */ }
            wakeRecRef.current = null;
            setWakeActive(false);
            if (tail && tail.length >= 2) {
              // Got a follow-on command in the same breath — submit
              // it directly without showing the overlay first.
              setOpen(true);
              setTranscript(tail.slice(0, MAX_TRANSCRIPT_CHARS));
              setError(null);
              setResponse(null);
              submit(tail);
            } else {
              // Bare wake phrase — open the overlay and start the
              // active mic so the user can speak the command now.
              voiceSpeak("Yes?");
              setTimeout(() => startListening(), 250);
            }
            return; // single match per session
          }
        };
        rec.onerror = (e) => {
          const code = e?.error;
          // 'no-speech' fires constantly in quiet rooms; ignore.
          if (code === "not-allowed" || code === "service-not-allowed") {
            setError("Microphone blocked — wake-word mode disabled.");
            setWakeEnabled(false);
          }
          // Other errors → just let onend retry.
        };
        rec.onend = () => {
          wakeRecRef.current = null;
          setWakeActive(false);
          if (cancelled || !wakeEnabled) return;
          // Restart with a small delay so we don't tightloop.
          wakeRestartTimerRef.current = setTimeout(startWake, 350);
        };

        wakeRecRef.current = rec;
        rec.start();
      } catch (e) {
        // start() throws if another recognizer is alive — back off.
        wakeRestartTimerRef.current = setTimeout(startWake, 800);
      }
    };

    startWake();

    return () => {
      cancelled = true;
      if (wakeRecRef.current) {
        try { wakeRecRef.current.abort(); } catch (_) { /* noop */ }
        wakeRecRef.current = null;
      }
      if (wakeRestartTimerRef.current) {
        clearTimeout(wakeRestartTimerRef.current);
        wakeRestartTimerRef.current = null;
      }
      setWakeActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeEnabled, SR, hidden, audioPlaying]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOpen = () => {
      dismissHint();
      try { window.localStorage.removeItem(HIDDEN_KEY); } catch (_) {}
      setHidden(false);
      setOpen(true);
      if (!listening) startListening();
    };
    window.addEventListener("holywords:open-voice", onOpen);
    return () => window.removeEventListener("holywords:open-voice", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening]);

  // Pause / resume wake-word listening when a DictationButton is active
  // anywhere on the page — otherwise the two listeners fight for the
  // mic and double-capture audio. Fires from DictationButton via
  // `hw:voice:pause` and `hw:voice:resume` window events.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const wasListeningRef = { current: false };
    const onPause = () => {
      wasListeningRef.current = listening;
      if (listening) stopListening();
    };
    const onResume = () => {
      if (wasListeningRef.current) {
        setTimeout(() => startListening(), 250);
      }
    };
    window.addEventListener("hw:voice:pause", onPause);
    window.addEventListener("hw:voice:resume", onResume);
    return () => {
      window.removeEventListener("hw:voice:pause", onPause);
      window.removeEventListener("hw:voice:resume", onResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening]);

  const toggleWake = () => {
    if (!SR) {
      setError("Wake-word needs Chrome, Edge, or Safari.");
      setOpen(true);
      return;
    }
    setWakeEnabled((v) => !v);
  };

  return (
    <>
      {/* Whole-feature kill switch. When the user dismisses the mic
          via the "×" close, we render absolutely nothing so the
          floating pill and the wake-word panel + green pulse all
          disappear. Wake-word recognizer is shut down by the effect
          above when `hidden` flips to true. Re-enable via Settings. */}
      {hidden ? null : (
      <>
      {/* Floating mic removed June 2026 per owner request ("the orange
          mic is getting in the way") — voice now opens from the inline
          mic buttons in the header and under every video, via the
          `holywords:open-voice` event. */}

      {/* Help / cheat-sheet drawer */}
      {showHelp && (
        <div className="fixed inset-0 z-[70] bg-ink/70 flex items-end md:items-center justify-center p-0 md:p-6"
             onClick={() => setShowHelp(false)} data-testid="voice-help-drawer">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full md:max-w-lg bg-cream rounded-t-3xl md:rounded-3xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-cream/95 backdrop-blur-sm flex items-center justify-between px-6 py-4 border-b border-forest/10">
              <div className="flex items-center gap-2">
                <Microphone size={20} weight="fill" className="text-terracotta" />
                <h2 className="font-heading text-2xl text-forest">Voice Commands</h2>
              </div>
              <button onClick={() => setShowHelp(false)}
                      data-testid="voice-help-close"
                      className="w-9 h-9 grid place-items-center rounded-full bg-forest/10 hover:bg-forest/20">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-forest/80 text-sm leading-relaxed">
                Tap the <span className="font-semibold text-terracotta">mic</span> button in the top bar (or under any video), wait for the red glow, then speak. Or long-press the wake toggle in the panel to enable <span className="italic">"Hey Holy Words"</span> hands-free mode.
              </p>
              {VOICE_EXAMPLES.map((g) => (
                <div key={g.group}>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-terracotta mb-2">
                    {g.group}
                  </div>
                  <ul className="space-y-1.5">
                    {g.items.map((ex) => (
                      <li key={ex}
                          className="px-4 py-2 rounded-xl bg-white border border-forest/10 text-sm text-forest font-medium">
                        <span className="text-forest/40 mr-2">"</span>{ex}<span className="text-forest/40 ml-1">"</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <p className="text-xs text-forest/60 italic">
                Voice recognition works in Chrome, Edge, Safari (iOS 14.3+), and Android Chrome. Spoken responses use OpenAI TTS when available.
              </p>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed z-[59] inset-0 grid place-items-end justify-end p-5 md:p-7 pointer-events-none"
          data-testid="voice-overlay"
        >
          <div
            className="pointer-events-auto w-[min(420px,calc(100vw-2.5rem))] mb-24 md:mb-28 mr-1 rounded-3xl bg-forest text-cream shadow-2xl overflow-hidden border border-cream/15"
          >
            <div className="px-5 py-4 flex items-center gap-3 border-b border-cream/10">
              <Sparkle size={16} weight="fill" className="text-amber-300" />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-200 flex items-center gap-2">
                  Voice Commander
                  {wakeActive && (
                    <span className="inline-flex items-center gap-1 normal-case tracking-normal text-[9px] font-semibold text-emerald-300" data-testid="voice-wake-active">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Wake on
                    </span>
                  )}
                </div>
                <div className="text-sm text-cream/80">
                  {!supported
                    ? "Voice needs Chrome, Edge, or Safari"
                    : listening
                    ? "Listening… speak a command"
                    : busy
                    ? "Thinking…"
                    : response?.say
                    ? response.say
                    : error
                    ? error
                    : wakeEnabled
                    ? "Try: \"Hey Holy Words, play Lecrae\""
                    : "Try: \"play Lecrae\", \"read Genesis 3\", \"open apocrypha\""}
                </div>
              </div>
              <button
                onClick={toggleWake}
                data-testid="voice-wake-toggle"
                aria-label={wakeEnabled ? "Disable wake word" : "Enable wake word"}
                aria-pressed={wakeEnabled ? "true" : "false"}
                title={wakeEnabled ? `Wake word ON — say "Hey Holy Words"` : "Wake word OFF"}
                className={[
                  "w-9 h-9 grid place-items-center rounded-full transition-all",
                  wakeEnabled
                    ? "bg-amber-300 text-ink shadow-md hover:scale-110"
                    : "bg-cream/10 text-cream/80 hover:bg-cream/20",
                ].join(" ")}
              >
                {wakeEnabled ? <Ear size={14} weight="fill" /> : <EarSlash size={14} weight="bold" />}
              </button>
              <button
                onClick={() => { setOpen(false); stopListening(); resumeMedia(); }}
                data-testid="voice-overlay-close"
                aria-label="Close voice commander"
                className="w-9 h-9 grid place-items-center rounded-full bg-cream/10 hover:bg-cream/20"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-5 py-4 min-h-[68px]" data-testid="voice-transcript-area">
              {transcript ? (
                <p className="font-heading text-xl leading-snug text-cream">
                  &ldquo;{transcript}&rdquo;
                </p>
              ) : listening ? (
                <div className="flex items-center gap-2 text-cream/70 text-sm">
                  <SoundWave />
                  <span>Speak now…</span>
                </div>
              ) : busy ? (
                <div className="text-cream/60 text-xs tracking-widest uppercase">Processing…</div>
              ) : (
                <ul className="space-y-1 text-[12px] text-cream/65 list-disc pl-5">
                  {wakeEnabled && (
                    <li className="text-emerald-300 font-semibold">
                      "Hey Holy Words, play Danny Gokey" — hands-free
                    </li>
                  )}
                  <li>"play Danny Gokey" / "play next song"</li>
                  <li>"read Genesis 1" / "read Jasher"</li>
                  <li>"open sermons" / "go to apocrypha"</li>
                  <li>"shuffle" / "pause" / "loop"</li>
                </ul>
              )}
            </div>
            {error && (
              <div className="px-5 py-3 bg-red-500/10 border-t border-red-400/20 text-amber-200 text-xs flex items-center gap-2" data-testid="voice-error">
                <WarningCircle size={14} weight="fill" /> {error}
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </>
  );
}

function SoundWave() {
  // Tiny animated equaliser so the "Listening" state feels alive.
  return (
    <span className="inline-flex items-end gap-[3px] h-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-amber-300"
          style={{
            height: `${30 + (i % 3) * 30}%`,
            animation: `hwVoiceWave 0.9s ease-in-out ${i * 0.12}s infinite alternate`,
          }}
        />
      ))}
    </span>
  );
}
